import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getServiceConfigoptionPrices } from '@/modules/Service/services'
import { ServiceInformationProps } from '@/modules/Service/types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
    createStorageLFDColumns,
    transformStorageLFDData,
    type StorageLFDRow,
} from './storage-lfd-config'

const Information: React.FC<ServiceInformationProps> = ({ service }) => {
    const { t } = useTranslation()
    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.SERVICES_CONFIGOPTIONS_PRICES,
            service?.service_id,
        ],
        queryFn: () =>
            getServiceConfigoptionPrices({ serviceID: service?.service_id }),
    })

    // Check if any configoption has price
    const hasPrice = useMemo(() => {
        return !!data?.some((configoption) => configoption.commitment_value > 0)
    }, [data])

    // Transform data to rows
    const rows = useMemo(() => {
        if (!service || !data) return []
        return transformStorageLFDData({ service, data })
    }, [service, data])

    // Create columns with conditional visibility
    const columns = useMemo(() => {
        const cols = createStorageLFDColumns(t, hasPrice)
        return cols.map((col) => ({
            ...col,
            hidden:
                col.key === 'service_stopped_date'
                    ? !service?.service_stopped_date
                    : col.hidden,
        }))
    }, [t, hasPrice, service?.service_stopped_date])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-4">
            <DataTableCustomized<StorageLFDRow>
                columns={columns}
                rows={rows}
                rowKey={(row) => String(row?.service_configoption_id)}
                size="small"
                resizable
                emptyMessage={t('no-data', { defaultValue: 'No data' })}
            />
        </div>
    )
}

export default Information
